// frontend/src/hooks/useAttachments.ts - NEW ATTACHMENT-SPECIFIC HOOKS
// Entity-specific hooks for attachments separated from core useApi.ts
// Insert this as a new file: useAttachments.ts

import { useCallback } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  AttachmentListResponse,
  AttachmentDetailResponse,
  AttachmentUploadResponse,
  AttachmentUpdateResponse,
  
  // Attachment types
  Attachment,
  AttachmentQueryParams,
  AttachmentUploadRequest,
  AttachmentUpdateRequest,
  AttachmentType,
  ProcessingStatus,
  VirusScanStatus,
  StorageTier,
  
  // Query parameter types
  QueryParams
} from '@/api/types';

// ===== LOCAL INTERFACES =====

/**
 * Extended attachment upload data with additional features
 */
interface ExtendedAttachmentUploadRequest extends AttachmentUploadRequest {
  metadata?: {
    uploadedBy?: string;
    uploadSession?: string;
    batchId?: string;
  };
}

/**
 * Extended attachment update data
 */
interface ExtendedAttachmentUpdateRequest extends AttachmentUpdateRequest {
  preserveHistory?: boolean;
  updateReason?: string;
}

/**
 * Attachment statistics interface
 */
interface AttachmentStats {
  totalAttachments: number;
  totalSize: number;
  byType: Record<AttachmentType, number>;
  byProcessingStatus: Record<ProcessingStatus, number>;
  byVirusScanStatus: Record<VirusScanStatus, number>;
  downloadCount: number;
  storageBreakdown: {
    emailAttachments: number;
    correspondenceAttachments: number;
    responseAttachments: number;
    staticDocuments: number;
  };
}

/**
 * Download URL response interface
 */
interface DownloadUrlResponse {
  url: string;
  expiresAt: string;
}

// ===== ATTACHMENT QUERY HOOKS =====

/**
 * Fetch paginated list of attachments with comprehensive filtering
 */
export const useAttachments = (params?: AttachmentQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Attachment>> => {
      
      const response = await apiClient.attachments.list(params);
      
      return normalizePaginatedResponse<Attachment>(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      retryCount: 2,
      retryDelay: 1000,
      rateLimitKey: 'attachments_list',
      ...options
    }
  );
};

/**
 * Fetch single attachment by ID with comprehensive details
 */
export const useAttachment = (id?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<Attachment> => {
      validateObjectId(id, 'Attachment ID');
      
      
      const response = await apiClient.attachments.get(id!);
      
      return normalizeSingleResponse<Attachment>(response);
    },
    {
      immediate: !!id,
      rateLimitKey: `attachment_${id}`,
      ...options
    }
  );
};

/**
 * Search attachments with advanced filtering
 */
export const useSearchAttachments = (query?: string, filters?: any, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Attachment>> => {
      if (!query?.trim()) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      
      // Use search in params with list method
      const searchParams = { ...filters, search: query };
      const response = await apiClient.attachments.list(searchParams);
      
      
      return normalizePaginatedResponse<Attachment>(response);
    },
    {
      immediate: !!query?.trim(),
      dependencies: [query, JSON.stringify(filters || {})],
      debounce: 500,
      rateLimitKey: 'attachments_search',
      ...options
    }
  );
};

// ===== ATTACHMENT ANALYTICS HOOKS =====

/**
 * Get attachment statistics
 */
export const useAttachmentStats = (options?: UseApiOptions) => {
  return useApi(
    async (): Promise<AttachmentStats> => {
      
      const response = await apiClient.attachments.getStats();
      
      return normalizeSingleResponse<AttachmentStats>(response);
    },
    {
      immediate: true,
      rateLimitKey: 'attachment_stats',
      ...options
    }
  );
};

/**
 * Get storage analytics
 */
export const useAttachmentStorageAnalytics = (options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      
      const response = await apiClient.attachments.getStorageAnalytics();
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: true,
      rateLimitKey: 'attachment_storage_analytics',
      ...options
    }
  );
};

// ===== ATTACHMENT PROCESSING HOOKS =====

/**
 * Get processing status for attachment
 */
export const useAttachmentProcessingStatus = (id?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      validateObjectId(id, 'Attachment ID');
      
      
      const response = await apiClient.attachments.getProcessingStatus(id!);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: !!id,
      rateLimitKey: `attachment_processing_${id}`,
      ...options
    }
  );
};

// ===== ATTACHMENT MUTATION HOOKS =====

/**
 * Upload attachments with enhanced validation
 */
export const useUploadAttachments = (options?: UseMutationOptions<Attachment[], ExtendedAttachmentUploadRequest>) => {
  return useMutation(
    async (data: ExtendedAttachmentUploadRequest): Promise<Attachment[]> => {
      
      // Enhanced client-side validation
      if (!data.files || !Array.isArray(data.files) || data.files.length === 0) {
        throw new Error('At least one file is required for upload');
      }
      
      // Validate file sizes (max 100MB per file)
      const maxFileSize = 100 * 1024 * 1024; // 100MB
      for (let i = 0; i < data.files.length; i++) {
        const file = data.files[i];
        if (file.size > maxFileSize) {
          throw new Error(`File "${file.name}" exceeds maximum size limit of 100MB`);
        }
        if (file.size === 0) {
          throw new Error(`File "${file.name}" is empty`);
        }
      }
      
      // Validate reference IDs if provided
      if (data.emailId) {
        validateObjectId(data.emailId, 'Email ID');
      }
      if (data.correspondenceId) {
        validateObjectId(data.correspondenceId, 'Correspondence ID');
      }
      if (data.responseId) {
        validateObjectId(data.responseId, 'Response ID');
      }
      
      // Validate tags if provided
      if (data.tags && Array.isArray(data.tags)) {
        data.tags.forEach((tag: string, index: number) => {
          if (typeof tag !== 'string' || tag.trim().length === 0) {
            throw new Error(`Tag ${index + 1} must be a non-empty string`);
          }
          if (tag.length > 50) {
            throw new Error(`Tag ${index + 1} cannot exceed 50 characters`);
          }
        });
      }
      
      // Remove extended fields for API call
      const { metadata, ...apiData } = data;
      
      const response = await apiClient.attachments.upload(apiData);

      // The API returns StandardResponse with data.attachments array
      // Each item has { success: boolean, attachment?: Attachment, error?: string }
      const normalized = normalizeSingleResponse<any>(response);

      // Extract the attachments array from the upload results
      const uploadResults = normalized.attachments || normalized || [];

      // Return the full upload results so caller can check success/failure
      return uploadResults;
    }, 
    options
  );
};

/**
 * Update attachment with validation
 */
export const useUpdateAttachment = (options?: UseMutationOptions<Attachment, { id: string; data: ExtendedAttachmentUpdateRequest }>) => {
  return useMutation(
    async ({ id, data }): Promise<Attachment> => {
      validateObjectId(id, 'Attachment ID');
      
      
      // Validate filename if provided
      if (data.filename !== undefined) {
        if (!data.filename || typeof data.filename !== 'string' || data.filename.trim().length === 0) {
          throw new Error('Filename cannot be empty');
        }
        if (data.filename.length > 255) {
          throw new Error('Filename cannot exceed 255 characters');
        }
      }
      
      // Validate visibility groups if provided
      if (data.visibilityGroups && Array.isArray(data.visibilityGroups)) {
        data.visibilityGroups.forEach((groupId: string, index: number) => {
          validateObjectId(groupId, `Visibility Group ${index + 1} ID`);
        });
      }
      
      // Validate tags if provided
      if (data.tags && Array.isArray(data.tags)) {
        data.tags.forEach((tag: string, index: number) => {
          if (typeof tag !== 'string' || tag.trim().length === 0) {
            throw new Error(`Tag ${index + 1} must be a non-empty string`);
          }
          if (tag.length > 50) {
            throw new Error(`Tag ${index + 1} cannot exceed 50 characters`);
          }
        });
      }
      
      // Remove extended fields for API call
      const { preserveHistory, updateReason, ...apiData } = data;
      
      const response = await apiClient.attachments.update(id, apiData);
      
      return normalizeSingleResponse<Attachment>(response);
    },
    options
  );
};

/**
 * Delete attachment
 */
export const useDeleteAttachment = (options?: UseMutationOptions<void, string>) => {
  return useMutation(
    async (id: string): Promise<void> => {
      validateObjectId(id, 'Attachment ID');
      
      
      const response = await apiClient.attachments.delete(id);
      
      return normalizeSingleResponse<void>(response);
    },
    options
  );
};

// ===== ATTACHMENT FILE OPERATION HOOKS =====

/**
 * Download attachment
 */
export const useDownloadAttachment = (options?: UseMutationOptions<Blob, string>) => {
  return useMutation(
    async (id: string): Promise<Blob> => {
      validateObjectId(id, 'Attachment ID');
      
      
      const response = await apiClient.attachments.download(id);
      
      return response;
    },
    options
  );
};

/**
 * Get download URL for attachment
 */
export const useGetAttachmentDownloadUrl = (options?: UseMutationOptions<DownloadUrlResponse, string>) => {
  return useMutation(
    async (id: string): Promise<DownloadUrlResponse> => {
      validateObjectId(id, 'Attachment ID');
      
      
      const response = await apiClient.attachments.getDownloadUrl(id);
      
      return normalizeSingleResponse<DownloadUrlResponse>(response);
    },
    options
  );
};

/**
 * Get presigned URL for attachment
 */
export const useGetAttachmentPresignedUrl = (options?: UseMutationOptions<DownloadUrlResponse, string>) => {
  return useMutation(
    async (id: string): Promise<DownloadUrlResponse> => {
      validateObjectId(id, 'Attachment ID');
      
      
      const response = await apiClient.attachments.getPresignedUrl(id);
      
      return normalizeSingleResponse<DownloadUrlResponse>(response);
    },
    options
  );
};

// ===== ATTACHMENT PROCESSING HOOKS =====

/**
 * Trigger attachment processing
 */
export const useTriggerAttachmentProcessing = (options?: UseMutationOptions<Attachment, string>) => {
  return useMutation(
    async (id: string): Promise<Attachment> => {
      validateObjectId(id, 'Attachment ID');
      
      
      const response = await apiClient.attachments.triggerProcessing(id);
      
      return normalizeSingleResponse<Attachment>(response);
    },
    options
  );
};

/**
 * Trigger virus scan for attachment
 */
export const useTriggerAttachmentVirusScan = (options?: UseMutationOptions<any, string>) => {
  return useMutation(
    async (id: string): Promise<any> => {
      validateObjectId(id, 'Attachment ID');
      
      
      const response = await apiClient.attachments.triggerVirusScan(id);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

// ===== ATTACHMENT MIGRATION HOOKS =====

/**
 * Copy attachment to correspondence
 */
export const useCopyAttachmentToCorrespondence = (options?: UseMutationOptions<Attachment, { id: string; correspondenceId: string }>) => {
  return useMutation(
    async ({ id, correspondenceId }): Promise<Attachment> => {
      validateObjectId(id, 'Attachment ID');
      validateObjectId(correspondenceId, 'Correspondence ID');
      
      
      const response = await apiClient.attachments.copyToCorrespondence(id, correspondenceId);
      
      return normalizeSingleResponse<Attachment>(response);
    },
    options
  );
};

/**
 * Migrate attachment to different type
 */
export const useMigrateAttachment = (options?: UseMutationOptions<Attachment, { id: string; targetType: string }>) => {
  return useMutation(
    async ({ id, targetType }): Promise<Attachment> => {
      validateObjectId(id, 'Attachment ID');
      
      if (!targetType || typeof targetType !== 'string') {
        throw new Error('Target type is required');
      }
      
      const validTargetTypes = ['email', 'correspondence', 'response', 'static'];
      if (!validTargetTypes.includes(targetType)) {
        throw new Error(`Invalid target type. Must be one of: ${validTargetTypes.join(', ')}`);
      }
      
      
      const response = await apiClient.attachments.migrate(id, targetType);
      
      return normalizeSingleResponse<Attachment>(response);
    },
    options
  );
};

// ===== BATCH OPERATION HOOKS =====

/**
 * Batch delete attachments
 */
export const useBatchDeleteAttachments = (options?: UseMutationOptions<any, string[]>) => {
  return useMutation(
    async (ids: string[]): Promise<any> => {
      if (!ids || !Array.isArray(ids) || ids.length === 0) {
        throw new Error('At least one attachment ID is required');
      }
      
      // Validate all attachment IDs
      ids.forEach((id, index) => {
        validateObjectId(id, `Attachment ${index + 1} ID`);
      });
      
      
      const response = await apiClient.attachments.batchDelete(ids);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Batch update attachments
 */
export const useBatchUpdateAttachments = (options?: UseMutationOptions<any, { 
  ids: string[]; 
  updates: Partial<AttachmentUpdateRequest> 
}>) => {
  return useMutation(
    async ({ ids, updates }): Promise<any> => {
      if (!ids || !Array.isArray(ids) || ids.length === 0) {
        throw new Error('At least one attachment ID is required');
      }
      
      if (!updates || typeof updates !== 'object') {
        throw new Error('Updates object is required');
      }
      
      // Validate all attachment IDs
      ids.forEach((id, index) => {
        validateObjectId(id, `Attachment ${index + 1} ID`);
      });
      
      // Validate visibility groups if provided in updates
      if (updates.visibilityGroups && Array.isArray(updates.visibilityGroups)) {
        updates.visibilityGroups.forEach((groupId: string, index: number) => {
          validateObjectId(groupId, `Visibility Group ${index + 1} ID`);
        });
      }
      
      // Validate tags if provided in updates
      if (updates.tags && Array.isArray(updates.tags)) {
        updates.tags.forEach((tag: string, index: number) => {
          if (typeof tag !== 'string' || tag.trim().length === 0) {
            throw new Error(`Tag ${index + 1} must be a non-empty string`);
          }
          if (tag.length > 50) {
            throw new Error(`Tag ${index + 1} cannot exceed 50 characters`);
          }
        });
      }
      
      
      const response = await apiClient.attachments.batchUpdate(ids, updates);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

// ===== UTILITY HOOKS =====

/**
 * Validate file before upload
 */
export const useValidateAttachmentFile = () => {
  return useCallback((file: File): { valid: boolean; error?: string } => {
    // Check file size (max 100MB)
    const maxFileSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxFileSize) {
      return { valid: false, error: `File "${file.name}" exceeds maximum size limit of 100MB` };
    }
    
    if (file.size === 0) {
      return { valid: false, error: `File "${file.name}" is empty` };
    }
    
    // Check file name length
    if (file.name.length > 255) {
      return { valid: false, error: `File name "${file.name}" exceeds maximum length of 255 characters` };
    }
    
    // Basic file type validation (can be extended)
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png',
      'image/gif',
      'text/plain',
      'application/zip'
    ];
    
    // Allow all file types for now, but log warning for uncommon types
    if (!allowedTypes.includes(file.type) && file.type) {
      console.warn(`File type "${file.type}" may not be supported for processing`);
    }
    
    return { valid: true };
  }, []);
};